import React from 'react';
import { Row, Col, Card, Form, Spinner, Badge, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faSave,
  faPaperPlane,
  faSearch,
  faFileInvoiceDollar,
  faCircleInfo,
  faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import ServerTable from '../../Components/Table/ServerTabe';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import { ConciliationApi, SapMasterApi } from '../../Services/ConciliationApiDataService';
import { formatCurrencyNum, formatDate } from '../../Utils/formaters';

const ActionBtn = ({ title, icon, onClick, disabled, variant = 'primary' }) => (
  <button className={`btn btn-${variant} fw-bold me-2`} onClick={onClick} disabled={disabled}>
    <FontAwesomeIcon icon={icon} className="me-1" />
    {title}
  </button>
);

const statusBadge = (status) => {
  const s = (status ?? '').toLowerCase();

  if (s === 'succeeded' || s === 'success') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'pending') return 'warning';
  if (s === 'skipped') return 'secondary';
  if (s === 'needsworkbench' || s === 'needsdata') return 'warning';
  if (s === 'completed') return 'success';
  return 'secondary';
};

const decisionBadge = (decision) => {
  if (!decision) return <Badge bg="secondary">No decision</Badge>;
  if (decision.isCompleted) return <Badge bg="success">Completed</Badge>;
  return <Badge bg="warning" text="dark">Draft</Badge>;
};

const fieldRow = (label, value) => (
  <div className="ofnm-field-row">
    <div className="ofnm-field-label">{label}</div>
    <div className="ofnm-field-value">{value ?? <span className="text-muted">-</span>}</div>
  </div>
);

const findOption = (options, value) =>
  value ? options.find(o => o.value === value) ?? null : null;

const isTrue = (value) => {
  if (value === true) return true;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

const isInformationalStatus = (status) => (status ?? '').toUpperCase() === 'OFICIALNOMATCHREL';

const rowStatus = (row) => row?.estadoConcilia ?? row?.EstadoConcilia;

const rowIsProcessed = (row) =>
  isTrue(row?.isProcessed) ||
  isTrue(row?.IsProcessed) ||
  !!row?.sapDocEntry ||
  !!row?.SapDocEntry;

const rowCanProcess = (row) => !rowIsProcessed(row) && !isInformationalStatus(rowStatus(row));

const isProcessedDetails = (details, workflow) => {
  if (isTrue(details?.isProcessed) || isTrue(details?.IsProcessed)) return true;
  if (details?.sapDocEntry) return true;

  return (workflow ?? []).some(a =>
    a.actionType === 'CreatePurchaseInvoice' && a.status === 'Succeeded'
  );
};

const getDecisionValue = (decision, camelKey) => {
  if (!decision) return null;
  const pascalKey = `${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
  return decision[camelKey] ?? decision[pascalKey] ?? null;
};

const decisionCellValue = (decision, key) => {
  const value = getDecisionValue(decision, key);
  return value ?? <span className="text-muted">-</span>;
};

const decisionLookupLabel = (decision, key, options) => {
  const code = getDecisionValue(decision, key);
  if (!code) return <span className="text-muted">-</span>;
  const match = (options ?? []).find((o) => o.value === code);
  return match?.label ?? code;
};

const OficialNoMatchWorkbenchTab = ({ companyId, getToken, showInfoModal }) => {
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [selectedKeys, setSelectedKeys] = React.useState(new Set());
  const [selectedKey, setSelectedKey] = React.useState(null);

  const [details, setDetails] = React.useState(null);
  const [workflow, setWorkflow] = React.useState([]);

  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const [processingBatch, setProcessingBatch] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState(null); // 'processBatch' | null
  const [loadingMasters, setLoadingMasters] = React.useState(false);

  const [vendorOptions, setVendorOptions] = React.useState([]);
  const [glOptions, setGlOptions] = React.useState([]);
  const [ccOptions, setCcOptions] = React.useState([]);
  const [drOptions, setDrOptions] = React.useState([]);

  const [quickSearch, setQuickSearch] = React.useState('');

  const [form, setForm] = React.useState({
    vendorCardCode: null,
    accountCode: null,
    taxCode: null,
    costingCode: null,
    costingCode2: null,
    docCurrency: 'QTZ',
    notes: ''
  });
  const activeCompanyRef = React.useRef(companyId);
  const detailsRequestSeqRef = React.useRef(0);

  React.useEffect(() => {
    activeCompanyRef.current = companyId;
  }, [companyId]);

  React.useEffect(() => {
    detailsRequestSeqRef.current += 1;
    setSelectedKey(null);
    setDetails(null);
    setWorkflow([]);
    setSelectedKeys(new Set());
    setLoadingDetails(false);
    setLoadingMasters(false);
    setVendorOptions([]);
    setGlOptions([]);
    setCcOptions([]);
    setDrOptions([]);
  }, [companyId]);

  React.useEffect(() => {
    if (!companyId) return;

    let cancelled = false;
    const requestCompanyId = companyId;

    (async () => {
      try {
        setLoadingMasters(true);
        const token = await getToken();

        const [vendors, gls, ccs, drs] = await Promise.all([
          SapMasterApi.getVendors(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getGlAccounts(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getCostCenters(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getDistributionRoutes(companyId, { search: '', page: 1, pageSize: 2000 }, token),
        ]);

        if (cancelled || activeCompanyRef.current !== requestCompanyId) return;

        setVendorOptions((vendors.items ?? []).map(v => ({
          value: v.cardCode,
          label: `${v.cardCode} - ${v.cardName ?? ''}`
        })));

        setGlOptions((gls.items ?? []).map(a => ({
          value: a.code,
          label: `${a.formatCode ?? a.code} - ${a.name ?? ''}`
        })));

        setCcOptions((ccs.items ?? []).map(c => ({
          value: c.centerCode,
          label: `${c.centerCode} - ${c.centerName ?? ''}`
        })));

        setDrOptions((drs.items ?? []).map(d => ({
          value: d.factorCode,
          label: `${d.factorCode} - ${d.factorDescription ?? ''}`
        })));
      } catch (e) {
        console.error('Failed loading OFICIALNOMATCH masters', e);
      } finally {
        if (!cancelled && activeCompanyRef.current === requestCompanyId) setLoadingMasters(false);
      }
    })();

    return () => { cancelled = true; };
  }, [companyId, getToken]);

  const fetchVendors = async () => {
    const token = await getToken();
    
  }

  const loadDetails = React.useCallback(async (conciliationKey) => {
    if (!conciliationKey) return;
    if (!activeCompanyRef.current) return;
    const requestId = detailsRequestSeqRef.current + 1;
    detailsRequestSeqRef.current = requestId;
    const requestCompanyId = activeCompanyRef.current;

    try {
      setLoadingDetails(true);
      const token = await getToken();

      const [d, actions] = await Promise.all([
        ConciliationApi.getOficialNoMatchWorkbench(conciliationKey, token),
        ConciliationApi.getWorkflowActionsByKey(conciliationKey, token)
      ]);
      if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;

      setDetails(d);
      setWorkflow(actions ?? []);

      const vendors = await SapMasterApi.getVendors(requestCompanyId, { search: '', page: 1, pageSize: 2000 }, token);
      if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;

      setVendorOptions((vendors.items ?? []).map(v => ({
        value: v.cardCode,
        label: `${v.cardCode} - ${v.cardName ?? ''}`
      })));

      // Workbench response decision is source-of-truth and may be auto-suggested by backend.
      const dec = d?.decision;
      setForm({
        vendorCardCode: dec?.vendorCardCode ?? dec?.VendorCardCode ?? null,
        accountCode: dec?.accountCode ?? dec?.AccountCode ?? null,
        taxCode: dec?.taxCode ?? dec?.TaxCode ?? null,
        costingCode: dec?.costingCode ?? dec?.CostingCode ?? null,
        costingCode2: dec?.costingCode2 ?? dec?.CostingCode2 ?? null,
        docCurrency: dec?.docCurrency ?? dec?.DocCurrency ?? 'QTZ',
        notes: dec?.notes ?? dec?.Notes ?? ''
      });
    } catch (e) {
      if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;
      console.error('Failed loading workbench details', e);
      showInfoModal('Error', <p className="mb-0">Could not load workbench details.</p>);
    } finally {
      if (requestId === detailsRequestSeqRef.current && activeCompanyRef.current === requestCompanyId) {
        setLoadingDetails(false);
      }
    }
  }, [getToken, showInfoModal]);

  const fetchOficialRows = React.useCallback(async ({ pageIndex, pageSize, globalFilter, sorting, columnFilters }) => {
    if (!companyId) return { items: [], totalItems: 0 };
    const requestCompanyId = companyId;

    const sort = sorting?.[0];
    const sortByMap = {
      goFecha: 'GOFecha',
      goProveedorID: 'GOProveedorID',
      goMonto: 'GOMonto',
      prioridadConcilia: 'PrioridadConcilia',
      estadoConcilia: 'EstadoConcilia'
    };
    const sortBy = sortByMap[sort?.id] ?? 'GOFecha';
    const sortDir = sort ? (sort?.desc ? 'desc' : 'asc') : 'desc';
    const columnFilterMap = {
      goFecha: 'GOFecha',
      goProveedorID: 'GOProveedorID',
      goMonto: 'GOMonto',
      prioridadConcilia: 'PrioridadConcilia',
      estadoConcilia: 'EstadoConcilia'
    };
    const columnFilterParams = {};
    (columnFilters ?? []).forEach((f) => {
      const key = columnFilterMap[f?.id];
      const value = (f?.value ?? '').toString().trim();
      if (key && value) columnFilterParams[key] = value;
    });
    const search = globalFilter || quickSearch || '';

    const token = await getToken();
    const res = await ConciliationApi.getConciliationGastos({
      companyId: requestCompanyId,
      status: 'OFICIALNOMATCH',
      page: pageIndex + 1,
      pageSize,
      search,
      sortBy,
      sortDir,
      ...columnFilterParams
    }, token);
    if (activeCompanyRef.current !== requestCompanyId) {
      return { items: [], totalItems: 0 };
    }

    const items = res.items ?? [];
    setRows(items);

    return {
      items,
      totalItems: res.totalCount ?? 0
    };
  }, [companyId, getToken, quickSearch, refreshTick]);

  const onSelectRow = async (row) => {
    setSelectedKey(row.conciliationKey);
    await loadDetails(row.conciliationKey);
  };

  const saveDecision = async () => {
    if (!selectedKey) return;
    if (isProcessedDetails(details, workflow)) return;

    try {
      setSaving(true);
      const token = await getToken();

      await ConciliationApi.saveOficialNoMatchDecision(selectedKey, form, token);
      await loadDetails(selectedKey);

      showInfoModal('Saved', <p className="mb-0">Decision saved successfully.</p>);
      setRefreshTick(t => t + 1);
    } catch (e) {
      console.error('Save decision failed', e);
      showInfoModal('Error', <p className="mb-0">Could not save decision.</p>);
    } finally {
      setSaving(false);
    }
  };

  const processRecord = async () => {
    if (!selectedKey) return;

    const detailStatus = details?.estadoConcilia ?? details?.EstadoConcilia;
    if (isInformationalStatus(detailStatus)) {
      showInfoModal(
        'Informational status',
        <p className="mb-0">Status OFICIALNOMATCHREL is informational in this tab and cannot be processed here.</p>
      );
      return;
    }

    const processed = isProcessedDetails(details, workflow);
    if (processed) return;

    try {
      setProcessing(true);
      const token = await getToken();
      const res = await ConciliationApi.processConciliationRecord(selectedKey, token);

      await loadDetails(selectedKey);
      setRefreshTick(t => t + 1);

      showInfoModal(
        'Process result',
        <div>
          <p className="mb-2"><strong>Status:</strong> {res.status}</p>
          {(res.messages ?? []).length > 0 && (
            <ul className="mb-0">
              {res.messages.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
      );
    } catch (e) {
      console.error('Process failed', e);
      showInfoModal('Error', <p className="mb-0">Could not process record.</p>);
    } finally {
      setProcessing(false);
    }
  };

  const processSelected = async () => {
    const keys = rows.filter((r) => selectedKeys.has(r.conciliationKey) && rowCanProcess(r)).map((r) => r.conciliationKey);
    if (!keys.length) return;

    try {
      setProcessingBatch(true);

      showInfoModal(
        'Processing started',
        <p className="mb-0">
          The selected records are being processed now. This may take a moment depending on how many were selected.
        </p>
      );

      const token = await getToken();
      const res = await ConciliationApi.processConciliationBatch(keys, token);

      setSelectedKeys(new Set());
      setRefreshTick((t) => t + 1);

      if (selectedKey) {
        await loadDetails(selectedKey);
      }

      showInfoModal(
        'Batch processing finished',
        <div>
          <p className="mb-2">
            Completed processing for <strong>{res.total}</strong> record(s).
          </p>
          <ul className="mb-2">
            <li>Succeeded: {res.succeeded}</li>
            <li>Failed: {res.failed}</li>
          </ul>

          {(res.items ?? []).some(x => !x.success) && (
            <>
              <strong>Failures:</strong>
              <ul className="mb-0 mt-1">
                {res.items
                  .filter(x => !x.success)
                  .slice(0, 10)
                  .map((x, i) => (
                    <li key={i}>
                      {x.conciliationKey} - {x.error ?? x.status}
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      );
    } catch (e) {
      console.error('Batch processing failed', e);
      showInfoModal('Error', <p className="mb-0">Could not process selected records.</p>);
    } finally {
      setProcessingBatch(false);
    }
  };

  const openProcessSelectedConfirm = () => {
    if (selectedKeys.size === 0) return;
    setConfirmAction('processBatch');
  };

  const closeConfirmModal = () => {
    setConfirmAction(null);
  };

  const onConfirmBatchAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);

    if (action === 'processBatch') {
      await processSelected();
    }
  };

  const selectableRows = React.useMemo(
    () => rows.filter((r) => rowCanProcess(r)),
    [rows]
  );

  const columns = React.useMemo(() => [
    {
      id: 'select',
      header: () => (
        <Form.Check
          type="checkbox"
          checked={
            selectableRows.length > 0 &&
            selectableRows.every(r => selectedKeys.has(r.conciliationKey))
          }
          onChange={(e) => {
            const next = new Set(selectedKeys);
            if (e.target.checked) selectableRows.forEach(r => next.add(r.conciliationKey));
            else selectableRows.forEach(r => next.delete(r.conciliationKey));
            setSelectedKeys(next);
          }}
        />
      ),
      cell: ({ row }) => (
        <Form.Check
          type="checkbox"
          checked={selectedKeys.has(row.original.conciliationKey)}
          disabled={!rowCanProcess(row.original)}
          onChange={(e) => {
            const next = new Set(selectedKeys);
            if (e.target.checked) next.add(row.original.conciliationKey);
            else next.delete(row.original.conciliationKey);
            setSelectedKeys(next);
          }}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false
    },
    // {
    //   header: 'Status',
    //   accessorKey: 'estadoConcilia',
    //   cell: ({ row }) => {
    //     const status = row.original.estadoConcilia ?? row.original.EstadoConcilia;
    //     return <Badge bg={statusBadge(status)}>{status}</Badge>;
    //   }
    // },
    // {
    //   header: 'Decision',
    //   id: 'decisionStatus',
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   cell: ({ row }) => (
    //     row.original.hasCompletedDecision
    //       ? <Badge bg="success">Completed</Badge>
    //       : row.original.hasDecision
    //         ? <Badge bg="warning" text="dark">Draft</Badge>
    //         : <Badge bg="secondary">None</Badge>
    //   )
    // },
    {
      header: 'Processed',
      id: 'processed',
      // enableSorting: false,
      // enableColumnFilter: false,
      cell: ({ row }) => (
        rowIsProcessed(row.original)
          ? <Badge bg="success">Yes</Badge>
          : <Badge bg="secondary">No</Badge>
      )
    },
    { header: 'Date', accessorKey: 'goFecha', cell: info => formatDate(info.getValue()) },
    // { header: 'Authorization', accessorKey: 'goNumeroAutorizacion' },
    { header: 'NIT', accessorKey: 'goProveedorID' },
    { header: 'Amount', accessorKey: 'goMonto', cell: info => <div className="text-end">{formatCurrencyNum(info.getValue())}</div> },
    // { header: 'Priority', accessorKey: 'prioridadConcilia' },
    {
      header: 'Vendor',
      id: 'decisionVendor',
      // enableSorting: false,
      // enableColumnFilter: false,
      cell: ({ row }) => decisionLookupLabel(row.original.decision, 'vendorCardCode', vendorOptions)
    },
    {
      header: 'Account',
      id: 'decisionGl',
      // enableSorting: false,
      // enableColumnFilter: false,
      cell: ({ row }) => decisionLookupLabel(row.original.decision, 'accountCode', glOptions)
    },
    {
      header: 'Tax Code',
      id: 'decisionTax',
      // enableSorting: false,
      // enableColumnFilter: false,
      cell: ({ row }) => decisionCellValue(row.original.decision, 'taxCode')
    },
    {
      header: 'Costing Code',
      id: 'decisionCc',
      // enableSorting: false,
      // enableColumnFilter: false,
      cell: ({ row }) => decisionCellValue(row.original.decision, 'costingCode')
    },
    {
      header: 'Costing Code 2',
      id: 'decisionCc2',
      // enableSorting: false,
      // enableColumnFilter: false,
      cell: ({ row }) => decisionCellValue(row.original.decision, 'costingCode2')
    },
  ], [rows, selectableRows, selectedKeys, vendorOptions, glOptions]);

  const processed = isProcessedDetails(details, workflow);
  return (
    <div className="ofnm-tab-shell">
      <div className="ofnm-pane ofnm-left">
        <Card className="h-100 shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="fw-bold">
              <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
              Invoices with no record on SAP
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <InputGroup size="sm" style={{ width: 260 }}>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search authorization, vendor, amount..."
                  value={quickSearch}
                  onChange={(e) => setQuickSearch(e.target.value)}
                />
              </InputGroup>

              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setRefreshTick(t => t + 1)}
                title="Refresh"
              >
                <FontAwesomeIcon icon={faRefresh} />
              </button>

              <button
                className="btn btn-sm btn-warning"
                onClick={openProcessSelectedConfirm}
                disabled={selectedKeys.size === 0 || processingBatch}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                Process Selected
              </button>
            </div>
          </Card.Header>

          <Card.Body className="ofnm-card-body p-0">
            <div className="ofnm-scroll">
              <ServerTable
                columns={columns}
                fetchData={fetchOficialRows}
                tableClass="table-hover text-nowrap no-select"
                containerClass="table-responsive"
                headerClass="table-light text-center"
                enableGlobalFilter={false}
                initialPageSize={20}
                onRowDoubleClick={onSelectRow}
                getRowClassName={(row) => (selectedKey === row.conciliationKey ? 'table-primary' : '')}
              />
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="ofnm-pane ofnm-right">
        <Card className="h-100 shadow-sm position-relative">
          {processing && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white"
              style={{ zIndex: 10, opacity: 0.9 }}
            >
              <Spinner animation="border" className="mb-3" />
              <div className="fw-semibold">Processing in background...</div>
              <div className="text-muted small">Please wait while SAP posting completes.</div>
            </div>
          )}
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="fw-bold">
              <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
              Workbench Details
            </div>

            <div className="d-flex flex-wrap gap-2">
              <ActionBtn
                title="Refresh"
                icon={faRefresh}
                variant="outline-primary"
                onClick={() => selectedKey && loadDetails(selectedKey)}
                disabled={!selectedKey || loadingDetails}
              />
              <ActionBtn
                title="Save"
                icon={faSave}
                onClick={saveDecision}
                disabled={!selectedKey || saving || processed}
              />
              <ActionBtn
                title="Process"
                icon={faPaperPlane}
                variant="warning"
                onClick={processRecord}
                disabled={!selectedKey || processing || processed}
              />
            </div>
          </Card.Header>

          <Card.Body className="ofnm-card-body">
            <div className="ofnm-scroll ofnm-details-scroll">
              {!selectedKey && (
                <div className="text-muted">
                  Double-click a row from the table to open the workbench.
                </div>
              )}

              {loadingDetails && (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" />
                </div>
              )}

              {!loadingDetails && details && (
                <>
                  {processed && (
                    <div className="alert alert-success mb-3">
                      This record has already been processed in SAP. The decision is now read-only.
                    </div>
                  )}

                  <div className="ofnm-summary-grid">
                    <Card className="shadow-sm">
                      <Card.Header className="fw-bold">Tributary Invoice</Card.Header>
                      <Card.Body>
                        {fieldRow('Authorization', details.goNumeroAutorizacion)}
                        {fieldRow('Serie', details.satSerie)}
                        {fieldRow('DTE Number', details.satNumeroDte)}
                        {fieldRow('Vendor', details.satNombreEmisor)}
                        {fieldRow('NIT', details.satNitEmisor)}
                        {fieldRow('Date', formatDate(details.satFechaEmision))}
                        {fieldRow('Total', formatCurrencyNum(details.satGranTotal))}
                        {fieldRow('Currency', details.satMoneda)}
                        {fieldRow('Blob Path', details.pdfBlobPath)}
                      </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                      <Card.Header className="fw-bold">Conciliation Status</Card.Header>
                      <Card.Body>
                        {fieldRow('Status', <Badge bg={statusBadge(details.estadoConcilia)}>{details.estadoConcilia}</Badge>)}
                        {fieldRow('Priority', details.prioridadConcilia)}
                        {fieldRow('Company', details.goEmpresaID ?? details.geEmpresaID)}
                        {fieldRow('ERP DocEntry', details.sapDocEntry)}
                        {fieldRow('Decision', decisionBadge(details.decision))}
                        {fieldRow('Processed', processed ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>)}
                      </Card.Body>
                    </Card>
                  </div>

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">Decision</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={12} className="mb-3">
                          <Form.Label>Vendor</Form.Label>
                          <Select
                            options={vendorOptions}
                            value={findOption(vendorOptions, form.vendorCardCode)}
                            onChange={(opt) => setForm(prev => ({ ...prev, vendorCardCode: opt?.value ?? null }))}
                            isClearable
                            isDisabled={loadingMasters || processed}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          />
                        </Col>

                        <Col md={12} className="mb-3">
                          <Form.Label>GL Account</Form.Label>
                          <Select
                            options={glOptions}
                            value={findOption(glOptions, form.accountCode)}
                            onChange={(opt) => setForm(prev => ({ ...prev, accountCode: opt?.value ?? null }))}
                            isClearable
                            isDisabled={loadingMasters || processed}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>Tax Code</Form.Label>
                          <Form.Select
                            value={form.taxCode ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, taxCode: e.target.value }))}
                            disabled={processed}
                          >
                            <option value="IVA">IVA</option>
                            <option value="EXE">EXE</option>
                          </Form.Select>
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>Document Currency</Form.Label>
                          <Form.Control
                            value={form.docCurrency ?? ''}
                            onChange={(e) => setForm(prev => ({ ...prev, docCurrency: e.target.value }))}
                            readOnly
                            disabled={processed}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>Costing Code</Form.Label>
                          <Select
                            options={ccOptions}
                            value={findOption(ccOptions, form.costingCode)}
                            onChange={(opt) => setForm(prev => ({ ...prev, costingCode: opt?.value ?? null }))}
                            isClearable
                            isDisabled={loadingMasters || processed}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>Costing Code 2</Form.Label>
                          <Select
                            options={drOptions}
                            value={findOption(drOptions, form.costingCode2)}
                            onChange={(opt) => setForm(prev => ({ ...prev, costingCode2: opt?.value ?? null }))}
                            isClearable
                            isDisabled={loadingMasters || processed}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          />
                        </Col>

                        <Col md={12} className="mb-2">
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={form.notes ?? ''}
                            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">
                      <FontAwesomeIcon icon={faClockRotateLeft} className="me-2" />
                      Workflow History
                    </Card.Header>
                    <Card.Body>
                      {(workflow ?? []).length === 0 && (
                        <div className="text-muted">No actions yet.</div>
                      )}

                      {(workflow ?? []).map(a => (
                        <div key={a.actionId} className="border rounded p-2 mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>{a.actionType}</strong>
                            <Badge bg={statusBadge(a.status)}>{a.status}</Badge>
                          </div>

                          <div className="small text-muted mt-1">
                            Attempts: {a.attemptCount} {a.lastAttemptAtUtc ? `| Last: ${a.lastAttemptAtUtc}` : ''}
                          </div>

                          {a.externalRef1 && <div className="small mt-1">Ref1: {a.externalRef1}</div>}
                          {a.externalRef2 && <div className="small">Ref2: {a.externalRef2}</div>}
                          {a.error && <div className="small text-danger mt-1">{a.error}</div>}
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      <ConfirmationModal
        show={!!confirmAction}
        onHide={closeConfirmModal}
        title="Confirm Process Selected"
        content={
          <div>
            <p className="mb-2">
              <>You are about to process <strong>{selectedKeys.size}</strong> selected record(s).</>
            </p>
            <p className="mb-0 text-muted">
              This action will run in batch for all currently selected keys.
            </p>
          </div>
        }
        onAcceptClick={onConfirmBatchAction}
        onCancelClick={closeConfirmModal}
      />
    </div>
  );
};

export default OficialNoMatchWorkbenchTab;
